import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapController, PopupControl, createAvatarIcon, createTaskMarkerIcon, InvalidateSizeOnLoad } from './MapComponents';
import { formatRelativeTime, formatDateUser } from '../../utils/dateUtils';
import { getPriorityIcon, getTaskTypeIcon, getTaskTypeLabel, getTaskStatusColor } from '../../utils/taskUtils';
import './TaskListMap.css';

const TaskListMap = ({ tasks, selectedMarker, handleTaskClick, setSelectedImage, employeeLocations }) => {
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [mapZoom, setMapZoom] = useState(2);
  const markerRef = useRef(null);

  const truncateText = (text, maxLength = 180) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength - 1).trim()}…`;
  };

  const linkifyText = (text) => {
    if (!text) return '';
    const parts = text.split(/(https?:\/\/[^\s]+)/gi);
    return parts.map((part, index) => {
      if (!part) return null;
      if (/^(https?:\/\/[^\s]+)$/i.test(part)) {
        return (
          <a
            key={`popup-link-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return <span key={`popup-text-${index}`}>{part}</span>;
    });
  };

  useEffect(() => {
    const tasksWithLocation = tasks.filter(task => {
      const hasLocation = task.location?.coordinates?.lat && task.location?.coordinates?.lng;
      return hasLocation;
    });

    // If there's a selected marker, center on that task
    if (selectedMarker) {
      const selectedTask = tasks.find(task => task.id === selectedMarker);
      if (selectedTask?.location?.coordinates) {
        setMapCenter([selectedTask.location.coordinates.lat, selectedTask.location.coordinates.lng]);
        setMapZoom(15);
        return;
      }
    }

    // Otherwise, calculate center based on all tasks
    if (tasksWithLocation.length > 0) {
      const totalLat = tasksWithLocation.reduce((sum, task) => sum + task.location.coordinates.lat, 0);
      const totalLng = tasksWithLocation.reduce((sum, task) => sum + task.location.coordinates.lng, 0);
      const center = [totalLat / tasksWithLocation.length, totalLng / tasksWithLocation.length];
      
      let zoom = 13;
      if (tasksWithLocation.length > 1) {
        const latSpread = Math.max(...tasksWithLocation.map(t => t.location.coordinates.lat)) - 
                         Math.min(...tasksWithLocation.map(t => t.location.coordinates.lat));
        const lngSpread = Math.max(...tasksWithLocation.map(t => t.location.coordinates.lng)) - 
                         Math.min(...tasksWithLocation.map(t => t.location.coordinates.lng));
        
        if (latSpread > 0.1 || lngSpread > 0.1) {
          zoom = 11;
        } else if (latSpread > 0.05 || lngSpread > 0.05) {
          zoom = 12;
        }
      }
      
      setMapCenter(center);
      setMapZoom(zoom);
    } else {
      // Default to a reasonable center if no tasks with location
      setMapCenter([0, 0]);
      setMapZoom(2);
    }
  }, [tasks, selectedMarker]);

  const tasksWithLocation = tasks.filter(task => {
    const hasLocation = task.location?.coordinates?.lat && task.location?.coordinates?.lng;
    return hasLocation;
  });

  const noLocationData = tasksWithLocation.length === 0 && (!employeeLocations || employeeLocations.length === 0);

  return (
    <div className="map-container">
      <MapContainer 
        center={mapCenter} 
        zoom={mapZoom} 
        style={{ height: '100%', width: '100%' }}
      >
        <InvalidateSizeOnLoad />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <MapController center={mapCenter} zoom={mapZoom} />
        
        {/* Task markers */}
        {tasksWithLocation.map(task => {
          const position = [task.location.coordinates.lat, task.location.coordinates.lng];
          const isSelected = selectedMarker === task.id;
          const truncatedDescription = truncateText(task.description);
          const renderedDescription = linkifyText(truncatedDescription);
          const departmentName = task.department?.name || task.department || 'Not assigned';
          const employeeName = task.employee
            ? `${task.employee.firstName || ''} ${task.employee.lastName || ''}`.trim()
            : 'Not assigned';
          const supervisorName = task.supervisor
            ? `${task.supervisor.firstName || ''} ${task.supervisor.lastName || ''}`.trim()
            : 'Not assigned';
          return (
            <Marker
              key={task.id}
              position={position}
              icon={createTaskMarkerIcon(task.status, isSelected)}
              ref={isSelected ? markerRef : null}
            >
              <Popup>
                <div 
                  className="map-popup"
                  onClick={() => handleTaskClick(task)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open task ${task.id} details`}
                >
                  <div className="popup-header">
                    <h3>{renderedDescription}</h3>
                    <div className="popup-badges">
                      <span 
                        className="task-status" 
                        style={{ backgroundColor: getTaskStatusColor(task.status) }}
                      >
                        {task.status.replace('-', ' ')}
                      </span>
                      {task.priority && (
                        <span className={`priority-badge priority-${task.priority.toLowerCase()}`}>
                          {task.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="popup-meta-rows">
                    <div className="popup-row">
                      <span className="popup-label">Customer</span>
                      <span className="popup-value customer-name">{task.customer?.name || 'N/A'}</span>
                      <span className="popup-label">Team</span>
                      <span className="popup-value department-name">{departmentName}</span>
                    </div>

                    <div className="popup-row">
                      <span className="popup-label">Created</span>
                      <span className="popup-value">{formatDateUser(task.createdAt)}</span>
                      <span className="popup-label">Due</span>
                      <span className="popup-value due-date">{task.dueDate ? formatDateUser(task.dueDate) : '-'}</span>
                    </div>

                    <div className="popup-row">
                      <span className="popup-label">Employee</span>
                      <span className="popup-value">{employeeName || 'Not assigned'}</span>
                      <span className="popup-label">Supervisor</span>
                      <span className="popup-value">{supervisorName || 'Not assigned'}</span>
                    </div>

                    {task.subtasks && task.subtasks.length > 0 && (
                      <div className="popup-row">
                        <span className="popup-label">Subtasks</span>
                        <span className="popup-value subtasks-count">{task.subtasks.length}</span>
                        <span className="popup-label">Progress</span>
                        <span className="popup-value">{task.completionPercentage ? `${task.completionPercentage}%` : '-'}</span>
                      </div>
                    )}

                    <div className="popup-row address-row">
                      <span className="popup-label">Address</span>
                      <span className="popup-value address">{task.location.address}</span>
                    </div>
                  </div>

                  {task.images && task.images.length > 0 && (
                    <div className="map-popup-images">
                      {task.images.map((image, index) => (
                        <img 
                          key={index}
                          src={image}
                          alt={`Task image ${index + 1}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(image);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </Popup>
              <PopupControl markerRef={markerRef} isSelected={isSelected} />
            </Marker>
          );
        })}
        
        {/* Employee location markers */}
        {employeeLocations && employeeLocations.filter(loc => loc.location && loc.location.coords).map((loc, idx) => {
          const coords = loc.location.coords;
          const employee = loc.employee;
          const initials = `${employee.firstName.charAt(0)}${employee.lastName.charAt(0)}`;
          return (
            <Marker
              key={`emp-${employee.id}`}
              position={[coords.latitude, coords.longitude]}
              icon={createAvatarIcon(employee.profilePicture, initials)}
            >
              <Popup>
                <div className="map-popup">
                  <h3>{employee.firstName} {employee.lastName}</h3>
                  <p><strong>Last updated:</strong> {formatRelativeTime(loc.lastUpdated)}</p>
                  <p><strong>Accuracy:</strong> {loc.location.accuracy ? `${Math.round(loc.location.accuracy)} meters` : 'N/A'}</p>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      {noLocationData && (
        <div className="empty-state">
          <div className="empty-icon">🗺️</div>
          <h3>No location data</h3>
          <p>Map is shown with default view. Add tasks or enable employee locations.</p>
        </div>
      )}
    </div>
  );
};

export default TaskListMap; 